import {
  Core,
  JOB_CHAIN_TEMPLATES,
  JOB_CHAIN_TEMPLATES_METADATA,
} from '@/api/Core';
import { httpClient } from '@/api/Core/Core';
import type {
  CreateJobChainTemplateRequest,
  JobChainTemplateDetail,
  JobChainTemplateDraftResult,
  JobChainTemplateGraph,
  JobChainTemplateValidation,
  JobChainTemplateApiResponse,
  JobChainTemplateListParams,
  JobChainTemplatePage,
  JobChainTemplateMetadata,
} from '@/api/JobChainTemplates/types';

const requirePositiveInteger = (name: string, value: number) => {
  if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite positive integer`);
  }
};

export const normalizeJobChainTemplateListParams = (
  params: JobChainTemplateListParams,
): JobChainTemplateListParams => {
  requirePositiveInteger('page', params.page);
  requirePositiveInteger('pageSize', params.pageSize);
  if (params.customerId !== undefined) {
    requirePositiveInteger('customerId', params.customerId);
  }
  if (params.version !== undefined) {
    requirePositiveInteger('version', params.version);
  }

  const jobChainType = params.jobChainType?.trim();

  return {
    ...(params.scope !== undefined && { scope: params.scope }),
    ...(params.customerId !== undefined && { customerId: params.customerId }),
    ...(jobChainType && { jobChainType }),
    ...(params.status !== undefined && { status: params.status }),
    ...(params.version !== undefined && { version: params.version }),
    page: params.page,
    pageSize: params.pageSize,
  };
};

class JobChainTemplates extends Core {
  getDetail = (id: number) =>
    this.get<JobChainTemplateDetail>(
      `${JOB_CHAIN_TEMPLATES()}/${id}`,
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateDetail>>;
  create = (request: CreateJobChainTemplateRequest) =>
    this.post<JobChainTemplateDetail>(
      JOB_CHAIN_TEMPLATES(),
      request,
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateDetail>>;
  createDraft = (id: number) =>
    this.post<JobChainTemplateDraftResult>(
      `${JOB_CHAIN_TEMPLATES()}/${id}/draft`,
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateDraftResult>>;
  saveGraph = (id: number, graph: JobChainTemplateGraph, etag: string) =>
    this.put<JobChainTemplateDetail>(
      `${JOB_CHAIN_TEMPLATES()}/${id}/graph`,
      graph,
      { headers: { 'If-Match': etag } },
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateDetail>>;
  validate = (id: number) =>
    this.post<JobChainTemplateValidation>(
      `${JOB_CHAIN_TEMPLATES()}/${id}/validate`,
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateValidation>>;
  publish = (
    id: number,
    request: { acknowledgedWarnings: string[] },
    etag: string,
  ) =>
    this.post<JobChainTemplateDetail>(
      `${JOB_CHAIN_TEMPLATES()}/${id}/publish`,
      request,
      { headers: { 'If-Match': etag } },
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateDetail>>;
  discard = (
    id: number,
    etag: string,
  ): Promise<JobChainTemplateApiResponse<void>> =>
    httpClient.delete(`${JOB_CHAIN_TEMPLATES()}/${id}`, {
      headers: { 'If-Match': etag },
    });
  listTemplates = async (
    params: JobChainTemplateListParams,
  ): Promise<JobChainTemplateApiResponse<JobChainTemplatePage>> =>
    this.get<JobChainTemplatePage>(
      JOB_CHAIN_TEMPLATES(),
      normalizeJobChainTemplateListParams(params),
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplatePage>>;

  getMetadata = async (): Promise<
    JobChainTemplateApiResponse<JobChainTemplateMetadata>
  > =>
    this.get<JobChainTemplateMetadata>(
      JOB_CHAIN_TEMPLATES_METADATA(),
    ) as Promise<JobChainTemplateApiResponse<JobChainTemplateMetadata>>;
}

export const jobChainTemplates = new JobChainTemplates();
